import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import MediaField from "../../_components/MediaField";
import { RALLY_STATUS_OPTIONS } from "../utils/rally.utils";
import CategorySelectList from "./CategorySelectList";

const RallyForm = ({
  form,
  validationErrors,
  submitting,
  submitLabel = "Create Rally",
  submittingLabel = "Creating...",
  isEdit = false,
  onSubmit,
  onFieldChange,
  onBack,
  categories = [],
  isCategoriesLoading = false,
  isCategoriesError = false,
}) => {
  const updateListItem = (field, index, patch) => {
    const next = [...(form[field] || [])];
    next[index] = { ...next[index], ...patch };
    onFieldChange(field, next);
  };

  const updateStringListItem = (field, index, value) => {
    const next = [...(form[field] || [])];
    next[index] = value;
    onFieldChange(field, next);
  };

  const addListItem = (field, emptyItem) => {
    onFieldChange(field, [...(form[field] || []), emptyItem]);
  };

  const removeListItem = (field, index, emptyItem) => {
    const current = [...(form[field] || [])];
    if (current.length <= 1) {
      onFieldChange(field, [typeof emptyItem === "function" ? emptyItem() : emptyItem]);
      return;
    }
    current.splice(index, 1);
    onFieldChange(field, current);
  };

  return (
    <Form onSubmit={onSubmit}>
      <h5 className="mb-3 mt-2">Basic Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              placeholder="Cholistan Desert Rally 2026"
              isInvalid={!!validationErrors.name}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Edition Number</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.edition_number}
              onChange={(e) => onFieldChange("edition_number", e.target.value)}
              placeholder="21"
              isInvalid={!!validationErrors.edition_number}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.edition_number}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Location <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.location}
              onChange={(e) => onFieldChange("location", e.target.value)}
              placeholder="Cholistan Desert"
              isInvalid={!!validationErrors.location}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.location}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Organiser</Form.Label>
            <Form.Control
              type="text"
              value={form.organiser}
              onChange={(e) => onFieldChange("organiser", e.target.value)}
              placeholder="TDCP"
            />
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Short description of the rally"
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Categories & Pricing</h5>
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Assign categories</Form.Label>
            <CategorySelectList
              categories={categories}
              selectedIds={form.categories || []}
              onChange={(next) => {
                onFieldChange("categories", next);
                const nextPricing = { ...(form.pricing || {}) };
                Object.keys(nextPricing).forEach((categoryId) => {
                  if (!next.includes(categoryId)) delete nextPricing[categoryId];
                });
                onFieldChange("pricing", nextPricing);
              }}
              pricing={form.pricing || {}}
              onPricingChange={(categoryId, amount) =>
                onFieldChange("pricing", {
                  ...(form.pricing || {}),
                  [categoryId]: amount,
                })
              }
              pricingErrors={validationErrors.pricing || {}}
              disabled={submitting}
              isLoading={isCategoriesLoading}
              isError={isCategoriesError}
            />
            <Form.Text className="text-muted">
              Select one or more categories and set a fee for each. Fees are
              sent as a prices map on save.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Dates</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>
              Start Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              value={form.date}
              onChange={(e) => onFieldChange("date", e.target.value)}
              isInvalid={!!validationErrors.date}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.date}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={form.end_date}
              onChange={(e) => onFieldChange("end_date", e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Rally Start Date</Form.Label>
            <Form.Control
              type="date"
              value={form.rally_start_date}
              onChange={(e) => onFieldChange("rally_start_date", e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Registration Start</Form.Label>
            <Form.Control
              type="date"
              value={form.registration_start_date}
              onChange={(e) =>
                onFieldChange("registration_start_date", e.target.value)
              }
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Registration End</Form.Label>
            <Form.Control
              type="date"
              value={form.registration_end_date}
              onChange={(e) =>
                onFieldChange("registration_end_date", e.target.value)
              }
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Status & Visibility</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={form.status}
              onChange={(e) => onFieldChange("status", e.target.value)}
            >
              {RALLY_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4} className="d-flex align-items-center">
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="is_featured"
              label="Featured rally"
              checked={Boolean(form.is_featured)}
              onChange={(e) => onFieldChange("is_featured", e.target.checked)}
            />
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Highlights</h5>
      <Row>
        <Col md={12}>
          {(form.highlights || []).map((highlight, index) => (
            <div key={`highlight-${index}`} className="d-flex gap-2 mb-2">
              <Form.Control
                type="text"
                value={highlight}
                onChange={(e) =>
                  updateStringListItem("highlights", index, e.target.value)
                }
                placeholder={`Highlight ${index + 1}`}
              />
              <Button
                type="button"
                variant="outline-danger"
                disabled={(form.highlights || []).length <= 1}
                onClick={() => removeListItem("highlights", index, "")}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline-primary"
            className="mb-3"
            onClick={() => addListItem("highlights", "")}
          >
            + Add Highlight
          </Button>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Stats</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Drivers</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.stats_drivers}
              onChange={(e) => onFieldChange("stats_drivers", e.target.value)}
              isInvalid={!!validationErrors.stats_drivers}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.stats_drivers}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Events</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.stats_events}
              onChange={(e) => onFieldChange("stats_events", e.target.value)}
              isInvalid={!!validationErrors.stats_events}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.stats_events}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Participants</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.stats_participants}
              onChange={(e) =>
                onFieldChange("stats_participants", e.target.value)
              }
              isInvalid={!!validationErrors.stats_participants}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.stats_participants}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Content</h5>
      <p className="text-muted small mb-3">
        Upload images to gallery first; the returned path is stored in the
        payload. Video links stay as URLs (e.g. YouTube).
      </p>
      <Row>
        <Col md={8}>
          <MediaField
            label="Cover Image"
            value={form.coverImage || ""}
            disabled={submitting}
            onChange={(next) => onFieldChange("coverImage", next)}
          />
        </Col>

        {!isEdit && (
          <Col md={4} className="d-flex align-items-center">
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="showContentAsGlobal"
                label="Copy image gallery to global Gallery"
                checked={Boolean(form.showContentAsGlobal)}
                onChange={(e) =>
                  onFieldChange("showContentAsGlobal", e.target.checked)
                }
              />
              <Form.Text className="text-muted">
                Only applied when creating a rally.
              </Form.Text>
            </Form.Group>
          </Col>
        )}
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Button Text 1</Form.Label>
            <Form.Control
              type="text"
              value={form.buttonText?.[0] || ""}
              onChange={(e) =>
                onFieldChange("buttonText", [
                  e.target.value,
                  form.buttonText?.[1] || "",
                ])
              }
              placeholder="Register Now"
              isInvalid={!!validationErrors.buttonText}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Button Text 2</Form.Label>
            <Form.Control
              type="text"
              value={form.buttonText?.[1] || ""}
              onChange={(e) =>
                onFieldChange("buttonText", [
                  form.buttonText?.[0] || "",
                  e.target.value,
                ])
              }
              placeholder="View Details"
              isInvalid={!!validationErrors.buttonText}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.buttonText}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Provide both labels, or leave both empty.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <h6 className="mb-2 mt-2">Image Gallery</h6>
      {(form.imagesGallery || []).map((url, index) => (
        <div
          key={`image-gallery-${index}`}
          className="border rounded p-3 mb-2"
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong className="small">Image #{index + 1}</strong>
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              disabled={(form.imagesGallery || []).length <= 1 || submitting}
              onClick={() => removeListItem("imagesGallery", index, "")}
            >
              Remove
            </Button>
          </div>
          <MediaField
            label=""
            value={url || ""}
            disabled={submitting}
            onChange={(next) =>
              updateStringListItem("imagesGallery", index, next)
            }
          />
        </div>
      ))}
      <Button
        type="button"
        variant="outline-primary"
        className="mb-3"
        disabled={submitting}
        onClick={() => addListItem("imagesGallery", "")}
      >
        + Add Image
      </Button>

      <h6 className="mb-2 mt-2">Video Gallery URLs</h6>
      {(form.videoGallery || []).map((url, index) => (
        <div key={`video-gallery-${index}`} className="d-flex gap-2 mb-2">
          <Form.Control
            type="url"
            value={url}
            onChange={(e) =>
              updateStringListItem("videoGallery", index, e.target.value)
            }
            placeholder="https://www.youtube.com/watch?v=..."
            disabled={submitting}
          />
          <Button
            type="button"
            variant="outline-danger"
            disabled={(form.videoGallery || []).length <= 1 || submitting}
            onClick={() => removeListItem("videoGallery", index, "")}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline-primary"
        className="mb-3"
        disabled={submitting}
        onClick={() => addListItem("videoGallery", "")}
      >
        + Add Video URL
      </Button>

      <h5 className="mb-3 mt-3">Documents</h5>
      {(form.documents || []).map((doc, index) => (
        <div
          key={`document-${index}`}
          className="border rounded p-3 mb-3"
        >
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Document URL</Form.Label>
                <Form.Control
                  type="url"
                  value={doc.documentUrl || ""}
                  onChange={(e) =>
                    updateListItem("documents", index, {
                      documentUrl: e.target.value,
                    })
                  }
                  placeholder="https://cdn.example.com/rules.pdf"
                  isInvalid={!!validationErrors.documents?.[index]}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={doc.title || ""}
                  onChange={(e) =>
                    updateListItem("documents", index, {
                      title: e.target.value,
                    })
                  }
                  placeholder="Official Rules"
                  isInvalid={!!validationErrors.documents?.[index]}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.documents?.[index]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <MediaField
                label="Background Image"
                value={doc.bgImage || ""}
                disabled={submitting}
                onChange={(next) =>
                  updateListItem("documents", index, {
                    bgImage: next,
                  })
                }
              />
            </Col>
            <Col md={4} className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id={`document-public-${index}`}
                label="Public"
                checked={doc.isPublic !== false}
                onChange={(e) =>
                  updateListItem("documents", index, {
                    isPublic: e.target.checked,
                  })
                }
              />
            </Col>
            <Col md={2} className="d-flex align-items-center justify-content-end">
              <Button
                type="button"
                variant="outline-danger"
                disabled={(form.documents || []).length <= 1}
                onClick={() =>
                  removeListItem("documents", index, {
                    documentUrl: "",
                    title: "",
                    bgImage: "",
                    isPublic: true,
                  })
                }
              >
                Remove
              </Button>
            </Col>
          </Row>
        </div>
      ))}
      <Button
        type="button"
        variant="outline-primary"
        className="mb-3"
        onClick={() =>
          addListItem("documents", {
            documentUrl: "",
            title: "",
            bgImage: "",
            isPublic: true,
          })
        }
      >
        + Add Document
      </Button>

      <h5 className="mb-3 mt-3">Destinations</h5>
      {(form.destinations || []).map((item, index) => (
        <div
          key={`destination-${index}`}
          className="border rounded p-3 mb-3"
        >
          <Row>
            <Col md={5}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={item.title || ""}
                  onChange={(e) =>
                    updateListItem("destinations", index, {
                      title: e.target.value,
                    })
                  }
                  placeholder="Derawar Fort"
                  isInvalid={!!validationErrors.destinations?.[index]}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.destinations?.[index]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={5}>
              <MediaField
                label="Image"
                value={item.image || ""}
                disabled={submitting}
                error={validationErrors.destinations?.[index]}
                onChange={(next) =>
                  updateListItem("destinations", index, {
                    image: next,
                  })
                }
              />
            </Col>
            <Col md={2} className="d-flex align-items-center justify-content-end">
              <Button
                type="button"
                variant="outline-danger"
                disabled={(form.destinations || []).length <= 1}
                onClick={() =>
                  removeListItem("destinations", index, {
                    title: "",
                    image: "",
                  })
                }
              >
                Remove
              </Button>
            </Col>
          </Row>
        </div>
      ))}
      <Button
        type="button"
        variant="outline-primary"
        className="mb-3"
        onClick={() => addListItem("destinations", { title: "", image: "" })}
      >
        + Add Destination
      </Button>

      <h5 className="mb-3 mt-3">Track</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={form.track?.title || ""}
              onChange={(e) =>
                onFieldChange("track", {
                  ...(form.track || {}),
                  title: e.target.value,
                })
              }
              placeholder="Main Desert Track"
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <MediaField
            label="Image"
            value={form.track?.image || ""}
            disabled={submitting}
            onChange={(next) =>
              onFieldChange("track", {
                ...(form.track || {}),
                image: next,
              })
            }
          />
        </Col>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.track?.description || ""}
              onChange={(e) =>
                onFieldChange("track", {
                  ...(form.track || {}),
                  description: e.target.value,
                })
              }
              placeholder="Open desert course"
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button type="button" variant="outline-secondary" onClick={onBack}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RallyForm;
